import { Link, useParams } from "react-router-dom";
import { api, kindLabel, microsToCredits } from "../api";
import { useResource } from "../resources";
import { formatPayload } from "../components/PayloadFormatter";
import RecordStatus from "../components/RecordStatus";

const tentative = (tx: Awaited<ReturnType<typeof api.tx>>) => tx.status === "TENTATIVE";

export default function TxPage() {
  const { id } = useParams<{ id: string }>();
  const resource = useResource(id, api.tx, tentative);
  const tx = resource.data;
  const status = <RecordStatus resource={resource} following={tx?.status === "TENTATIVE" && resource.error === null} />;
  if (!tx) return status;

  const status_color: Record<string, string> = {
    TENTATIVE: "text-mesh-warn",
    FINAL: "text-mesh-ok",
    INVALID: "text-mesh-err",
  };

  return (
    <div className="space-y-4">
      {status}
      <div>
        <h1 className="text-xl font-bold text-mesh-accent">transaction</h1>
        <div className="hex text-sm text-mesh-mute break-all">{tx.tx_id}</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="kind" value={kindLabel(tx.kind)} />
        <Field
          label="status"
          value={tx.status}
          className={status_color[tx.status]}
        />
        <Field
          label="round"
          value={tx.witness_round != null ? `r${tx.witness_round}` : "—"}
        />
        <Field
          label="amount"
          value={tx.kind === 0 ? `${microsToCredits(tx.amount)} cr` : "—"}
        />
        <Field
          label="sender"
          value={
            tx.sender ? (
              <Link
                to={`/account/${tx.sender}`}
                className="hex text-mesh-accent hover:underline"
              >
                {tx.sender}
              </Link>
            ) : (
              "—"
            )
          }
        />
        <Field
          label="receiver"
          value={
            tx.receiver ? (
              <Link
                to={`/account/${tx.receiver}`}
                className="hex text-mesh-accent hover:underline"
              >
                {tx.receiver}
              </Link>
            ) : (
              "—"
            )
          }
        />
        <Field label="logical_time" value={tx.logical_time} />
        <Field label="pow nonce" value={tx.pow} />
        <Field label="received_at" value={new Date(tx.received_at).toLocaleString()} />
        <Field
          label="finalized_at"
          value={
            tx.finalized_at
              ? new Date(tx.finalized_at).toLocaleString()
              : "—"
          }
        />
        <Field label="invalid_reason" value={tx.invalid_reason ?? "—"} />
        <Field label="community" value={tx.community} />
      </div>

      <section className="bg-mesh-panel/40 rounded-lg p-4">
        <h2 className="text-sm uppercase tracking-wider text-mesh-mute mb-2">
          parents ({tx.parents.length})
        </h2>
        <ul className="space-y-1 text-sm">
          {tx.parents.map((p) => (
            <li key={p}>
              <Link
                to={`/tx/${p}`}
                className="hex text-mesh-accent hover:underline"
              >
                {p}
              </Link>
            </li>
          ))}
          {tx.parents.length === 0 && (
            <li className="text-mesh-mute">none (genesis or witness)</li>
          )}
        </ul>
      </section>

      <section className="bg-mesh-panel/40 rounded-lg p-4 space-y-3">
        <h2 className="text-sm uppercase tracking-wider text-mesh-mute">
          payload
        </h2>
        {formatPayload(tx)}
      </section>

      <section className="bg-mesh-panel/40 rounded-lg p-4">
        <h2 className="text-sm uppercase tracking-wider text-mesh-mute mb-2">
          signature
        </h2>
        <div className="hex text-xs break-all">{tx.signature}</div>
        {tx.pubkey && (
          <>
            <div className="text-xs text-mesh-mute mt-2">pubkey</div>
            <div className="hex text-xs break-all">{tx.pubkey}</div>
          </>
        )}
      </section>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: React.ReactNode;
  className?: string;
}

function Field({ label, value, className }: FieldProps) {
  return (
    <div className="bg-mesh-panel rounded-lg p-3">
      <div className="text-xs uppercase text-mesh-mute">{label}</div>
      <div className={`text-sm break-all ${className ?? ""}`}>{value}</div>
    </div>
  );
}
